import { useEffect, useState, useCallback } from 'react';
import type { IFabricConnection, IFabricWorkspace, FabricConnectionStatus } from '@skystone/shared';
import { fabricSyncApi } from '@/services/fabric-sync-api';
import { getErrorMessage } from '@/lib/utils';
import { formatDateTime } from '@/lib/date-utils';
import { useFabricEnvironment } from '@/hooks/use-fabric-environment';
import { Badge, Button, Spinner, PageLoading, ErrorBanner, DevEnvironmentBanner } from '@/components/ui';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';

const STATUS_BADGE_MAP: Record<FabricConnectionStatus, { label: string; variant: 'success' | 'warning' | 'secondary' }> = {
  connected: { label: 'Connected', variant: 'success' },
  disconnected: { label: 'Disconnected', variant: 'warning' },
  not_configured: { label: 'Not Configured', variant: 'secondary' },
};

export function FabricSettingsTab() {
  const { isDev } = useFabricEnvironment();

  const [connection, setConnection] = useState<IFabricConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action states
  const [verifying, setVerifying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // Workspace
  const [workspace, setWorkspace] = useState<IFabricWorkspace | null>(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);

  const loadConnection = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fabricSyncApi.getConnection();
      setConnection(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to load Fabric connection status'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkspace = useCallback(async (): Promise<void> => {
    setWorkspaceLoading(true);
    setWorkspaceError(null);
    try {
      const response = await fabricSyncApi.getWorkspace();
      setWorkspace(response);
    } catch (err: unknown) {
      setWorkspaceError(getErrorMessage(err, 'Failed to load Fabric workspace'));
    } finally {
      setWorkspaceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  // Load workspace when connection becomes connected
  useEffect(() => {
    if (connection?.status === 'connected') {
      loadWorkspace();
    }
  }, [connection?.status, loadWorkspace]);

  const handleVerify = async (): Promise<void> => {
    setVerifying(true);
    setError(null);
    try {
      const response = await fabricSyncApi.verifyConnection();
      setConnection(response);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to verify Fabric connection'));
    } finally {
      setVerifying(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    setDisconnecting(true);
    setError(null);
    try {
      const response = await fabricSyncApi.disconnect();
      setConnection(response);
      setWorkspace(null);
      setWorkspaceError(null);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to disconnect Fabric'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return <PageLoading />;
  }

  const status = connection?.status ?? 'not_configured';
  const badge = STATUS_BADGE_MAP[status];
  const isConnected = status === 'connected';

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Configure the Microsoft Fabric integration for data lakehouse access.
      </p>

      <DevEnvironmentBanner isDev={isDev} />

      <ErrorBanner message={error} />

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg">Connection Status</CardTitle>
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
          <CardDescription>
            Manage the connection between Skystone and your Microsoft Fabric tenant.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'not_configured' && (
            <p className="text-sm text-muted-foreground">
              Set the{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">FABRIC_TENANT_ID</code>,{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">FABRIC_CLIENT_ID</code>,{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">FABRIC_CLIENT_SECRET</code>, and{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">FABRIC_WORKSPACE_ID</code>{' '}
              environment variables and restart the service, then click Verify Connection.
            </p>
          )}

          {isConnected && connection && (
            <div className="mb-4 space-y-1 text-sm">
              <p>
                <span className="font-medium text-foreground">Tenant ID:</span>{' '}
                <span className="text-muted-foreground">{connection.tenantId}</span>
              </p>
              {connection.connectedAt && (
                <p>
                  <span className="font-medium text-foreground">Connected At:</span>{' '}
                  <span className="text-muted-foreground">
                    {formatDateTime(connection.connectedAt)}
                  </span>
                </p>
              )}
            </div>
          )}

          {status === 'disconnected' && (
            <p className="mb-4 text-sm text-muted-foreground">
              The Fabric connection was previously active but has been disconnected. Click Verify Connection to reconnect.
            </p>
          )}

          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={handleVerify}
              isLoading={verifying}
              disabled={verifying || disconnecting || status === 'not_configured'}
            >
              Verify Connection
            </Button>
            {isConnected && (
              <Button
                variant="destructive"
                onClick={handleDisconnect}
                isLoading={disconnecting}
                disabled={verifying || disconnecting}
              >
                Disconnect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Workspace Section — only when connected */}
      {isConnected && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Workspace</CardTitle>
            <CardDescription>
              The configured Fabric workspace accessible to the service principal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {workspaceLoading && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
              </div>
            )}

            <ErrorBanner message={workspaceError} className="mb-4" />

            {!workspaceLoading && !workspaceError && !workspace && (
              <p className="text-sm text-muted-foreground">
                Could not access the configured workspace. Ensure the service principal is added as a member of the workspace in the Fabric portal.
              </p>
            )}

            {!workspaceLoading && workspace && (
              <div className="rounded-md border border-border bg-muted/50 px-4 py-3">
                <div className="flex items-center justify-between">
                  <p className="truncate font-medium text-foreground">{workspace.displayName}</p>
                  <Badge variant="secondary" className="ml-3 shrink-0">{workspace.state}</Badge>
                </div>
                {workspace.description && (
                  <p className="mt-1 truncate text-xs text-muted-foreground">{workspace.description}</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">ID:</span>{' '}
                  {workspace.id}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
